import { Tooltip } from 'antd';
import { CursorIcon } from './CursorIcon';
import styles from './MeasurementRow.module.css';

const isMac =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad/i.test(navigator.userAgent);
/** Converge accepts either modifier; the hint shows the platform's own. */
const modKey = isMac ? '⌘' : 'Ctrl';

interface CalculatedIndicatorProps {
  /** Humanized formula; null when the backend sent none. */
  formula: string | null;
  diverged: boolean;
  /** Formula result at display precision; null when it cannot be evaluated. */
  calculatedText: string | null;
  /** The row's value at display precision. */
  enteredText: string;
  /** Operand keys the formula could not resolve. */
  missingInputs?: string[];
  /** Ctrl/Cmd+click on the region resets the row to its formula.
   *  Absent, or `disabled`, ⇒ not offered. */
  onConverge?: () => void;
  disabled?: boolean;
}

/**
 * `fx` on every calculated row, `≠` after it when diverged. The
 * two share one hover card, laid out like the range card: state heading,
 * label/value rows, action under a divider. Ctrl/Cmd+click the region
 * converges.
 */
export function CalculatedIndicator({
  formula,
  diverged,
  calculatedText,
  enteredText,
  missingInputs,
  onConverge,
  disabled = false,
}: CalculatedIndicatorProps) {
  const canConverge = !!onConverge && !disabled;
  const convergeReady = canConverge && calculatedText !== null;
  // Plain click is inert — the modifier is what distinguishes intent from a stray hit.
  const convergeOnModifier = (e: { ctrlKey: boolean; metaKey: boolean }) => {
    if (!convergeReady || !(e.ctrlKey || e.metaKey)) return;
    onConverge?.();
  };
  const missingList = missingInputs?.join(', ') ?? '';

  const card = (
    <div className={styles.derivedTooltip}>
      <span
        className={`${styles.tooltipHeading} ${diverged ? '' : styles.tooltipHeadingOk}`}
      >
        {diverged ? 'Diverged' : 'Calculated'}
      </span>
      <span className={styles.tooltipRow}>
        <span className={styles.tooltipLabel}>Formula</span>
        <span className={`${styles.tooltipValue} ${styles.tooltipMono}`}>
          {formula ?? 'unavailable'}
        </span>
      </span>
      {diverged && (
        <>
          {missingList ? (
            <span className={styles.tooltipRow} data-testid="missing-inputs">
              <span className={styles.tooltipLabel}>Missing</span>
              <span className={`${styles.tooltipValue} ${styles.tooltipError}`}>
                {missingList}
              </span>
            </span>
          ) : (
            <span className={styles.tooltipRow} data-testid="calculated-value">
              <span className={styles.tooltipLabel}>Calculated</span>
              <span className={styles.tooltipValue}>{calculatedText}</span>
            </span>
          )}
          <span className={styles.tooltipRow} data-testid="entered-value">
            <span className={styles.tooltipLabel}>Entered</span>
            <span className={styles.tooltipValue}>{enteredText}</span>
          </span>
        </>
      )}
      {diverged && canConverge && (
        <span
          className={`${styles.tooltipAction} ${styles.tooltipDivider}`}
          data-testid="converge-hint"
        >
          {calculatedText === null ? (
            'Reset unavailable'
          ) : (
            <>
              <kbd className={styles.kbd}>{modKey}</kbd>+
              <kbd className={styles.kbd}>
                <CursorIcon />
              </kbd>
              to reset
            </>
          )}
        </span>
      )}
    </div>
  );

  return (
    <Tooltip
      title={card}
      // antd caps a tooltip at 250px; a long formula needs more before it wraps.
      styles={{ root: { maxWidth: 360 } }}
    >
      {/* Converge is a modifier+click gesture by spec with no keyboard
          equivalent, so the region is a plain hover target, not a button. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <span
        className={styles.calculatedGlyphs}
        onClick={convergeReady ? convergeOnModifier : undefined}
      >
        <span className={styles.fx} role="img" aria-label="Calculated value">
          fx
        </span>
        {diverged && (
          <span
            className={styles.diverged}
            role="img"
            aria-label="Diverged from calculated value"
          >
            ≠
          </span>
        )}
      </span>
    </Tooltip>
  );
}
